import React, { useEffect, useLayoutEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useOktaAuth } from "@okta/okta-react";
import { RootState } from "../../store/store";
import {
  resetForecastState,
  resetRcaState,
  setForecastFilter,
  setRcaFilter,
  setResultVisualizationFilter,
} from "../../store/filterSlice";
import { useForecastClusterFilter } from "../../api/filtersApi";
import { PAGE_LIMIT } from "../../utils/constant";
import {
  calculateTotalPages,
  rcaExecutionTableApi,
  rcaMetricsApi,
  rcaSummaryTableApi,
  rcaTreeGraphApi,
} from "../../api/forecastPageApi";
import {
  clearSelectedRows,
  resetExecutionBackTesting,
  resetMetrics,
  resetSummaryBackTestingPagination,
  resetTableData,
  resetTreeChartData,
  setMetrics,
  setRcaStats,
  setRcaTags,
  setRcaTreeGraph,
  setSecondTable,
  setTableData,
  setTableLoading,
  updateExecutionBackTestingPageNumber,
  updateSummaryBackTestingPageNumber,
} from "../../store/rcaSlice";
import FilterDropdown from "../../baseComponent/Filter/FilterDropdown";
import FilterButtons from "../../baseComponent/Filter/FilterButtons";
import FilterResetModal from "../../baseComponent/Filter/FilterResetModal";
import { resetForecastData } from "../../store/foreCastDataSlice";
import { resetForecastDecomState } from "../../store/foreCastDecomSlice";
import {
  resetForecastBackTestingApiDataLoading,
  resetForecastBackTestingAverageState,
  resetForecastBackTestingChartState,
  resetForecastBackTestingTableState,
} from "../../store/foreCastBackTestingDataSlice";
import { toArray } from "@amcharts/amcharts5/.internal/core/util/Array";

const RcaExecutionFilter: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { authState } = useOktaAuth();
  const dispatch = useDispatch();

  const userData = useSelector((state: RootState) => state.user);
  const filter = useSelector((state: RootState) => state.forecastFilter);
  const rcaExecutionPaginationSlice = useSelector(
    (state: RootState) => state.rcaExecutionPaginationSlice
  );

  const rcaSummaryPaginationSlice = useSelector(
    (state: RootState) => state.rcaSummaryPaginationSlice
  );

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      dispatch(resetMetrics());
      dispatch(resetTreeChartData());
      dispatch(resetTableData());
      dispatch(
        setRcaFilter({ key: "clusters", value: clusterApiData?.clusters })
      );
      dispatch(resetExecutionBackTesting());
      dispatch(resetSummaryBackTestingPagination());
    };
  }, []);

  const { data: clusterApiData } = useForecastClusterFilter(
    authState?.accessToken?.accessToken!,
    "cluster",
    userData?.currentSector ? userData.currentSector : {}
  );

  const payload = {
    shortTermMidTermCode: filter?.term.toString(),
    snapshotDate:
      filter?.term.toString() === "ST"
        ? filter?.shortSnapshotDate
        : filter?.midSnapshotDate,
    brandName: filter?.brand,
    subBrandName: filter?.subBrand,
    productLineName: filter?.productLine,
    productGroupName: filter?.productGroup,
    packageSizeName: filter?.size,
    keyAccount: filter?.keyAccount,
    materialGlobalTradeItemNumberDescription: filter?.planningItem,
    productCategoryName: filter?.category,
    eachUPC: filter?.eachUPC,
    portfolioGroupName: filter?.mg4PortfolioGroup,
    packageName: filter?.count,
    customerGlobalName: filter?.global,
    customerSectorNumber: filter?.customerSector,
    packTypeName: filter.ppg,
    customerGroupName: filter?.customerGroup,
    packageTypeName: filter?.container,
    customerBusinessUnitName: filter?.businessUnit,
    customerMarketUnitName: filter?.marketUnit,
    ibpDemandGroup: filter?.ibpDemandGroup,
    customerTradeSegmentDescription: filter?.cg1TrageSegment,
    salesSectorName: filter?.salesSector,
    salesRegionName: filter?.salesRegion,
    salesZoneName: filter?.zone,
    salesCountryCode: filter?.salesCountry,
    salesOrganizationCode: filter?.businessOrg,
    salesDivisionName: filter?.division,
    forecastEngineClusterId: filter?.clusters,
    forecastModuleCode: filter?.ac1ac2,
    forecastEngineSegmentationId: filter?.segment,
    pageNumber: 1,
    pageSize: PAGE_LIMIT,
    sector: userData?.currentSector?.sector,
    market: userData?.currentSector?.market,
    salesGeoLocationId:
      filter?.location?.map((loc) => loc.split(" - ")[0]) || [],
    salesGeoLocationName:
      filter?.location?.map((loc) => loc.split(" - ")[1]) || [],
    rcaTag: filter?.rcaTag.includes("All") ? [] : toArray(filter?.rcaTag),
    // LATAM Key
    flavorName: filter?.flavourNames,
    customerChannelName: filter?.channels,
    customerDemandGroup: filter?.demandGroups,
    customerSubChannelName: filter?.subChannels,
    customerRegionalCluster: filter?.regionalClusters,
    locationGlobalName: filter?.globalCode,
    locationSectorName: filter?.locationSector,
    locationCountryName: filter?.locationCountry,
    planningLocationId: filter?.planningLocationId,
    locationRegionName: filter?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
  };
  const payloadForTable = {
    ...payload,
  };

  const resetFilter = () => {
    dispatch(resetTreeChartData());
    dispatch(
      resetRcaState({ key: "clusters", value: clusterApiData?.clusters })
    );
    dispatch(resetMetrics());
    dispatch(resetTableData());
    dispatch(
      setRcaFilter({ key: "clusters", value: clusterApiData?.clusters })
    );

    dispatch(
      resetForecastState({ key: "clusters", value: clusterApiData?.clusters })
    );
    dispatch(resetForecastData());
    dispatch(resetForecastDecomState());
    dispatch(resetForecastBackTestingTableState());
    dispatch(resetForecastBackTestingChartState());
    dispatch(resetForecastBackTestingAverageState());
    dispatch(resetForecastBackTestingApiDataLoading());
    dispatch(clearSelectedRows());
    handleClose();

    if (userData?.currentSector?.sector === "PFNA") {
      dispatch(
        setRcaFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );
      dispatch(
        setForecastFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );

      dispatch(
        setResultVisualizationFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );
    }
  };

  useEffect(() => {
    if (
      rcaExecutionPaginationSlice?.totalPages > 1 &&
      rcaExecutionPaginationSlice?.isDataShownClicked === false
    ) {
      const rcaExecutionBackTestingApiRequestBody = {
        ...payloadForTable,
        pageNumber: rcaExecutionPaginationSlice?.pageNumber,
        pageSize: rcaExecutionPaginationSlice?.pageLimit,
      };
      makeBackTesingApiCall(rcaExecutionBackTestingApiRequestBody, authState);
    }
  }, [rcaExecutionPaginationSlice?.pageNumber]);

  useEffect(() => {
    if (
      rcaSummaryPaginationSlice?.totalPages > 1 &&
      rcaSummaryPaginationSlice?.isDataShownClicked === false
    ) {
      const rcaExecutionBackTestingApiRequestBody = {
        ...payloadForTable,
        pageNumber: rcaSummaryPaginationSlice?.pageNumber,
        pageSize: rcaSummaryPaginationSlice?.pageLimit,
      };
      makeSummaryCall(rcaExecutionBackTestingApiRequestBody, authState);
    }
  }, [rcaSummaryPaginationSlice?.pageNumber]);

  const applyFilters = async () => {
    rcaMetricsApi(payload, authState).then((dataMetrics) => {
      dispatch(setMetrics(dataMetrics));
    });
    rcaTreeGraphApi(payload, authState).then((dataTreeGraph) => {
      dispatch(setRcaTreeGraph(dataTreeGraph?.rcaTreeGraph));
    });
    makeBackTesingApiCall(payloadForTable, authState);
    makeSummaryCall(payloadForTable, authState);
  };

  const makeBackTesingApiCall = (
    forecastAnalysisApiRequestBodyTable: any,
    authState: any
  ) => {
    const resultVisulizationTestingApiRequestBody = {
      ...forecastAnalysisApiRequestBodyTable,
      pageNumber: rcaExecutionPaginationSlice?.pageNumber,
      pageSize: rcaExecutionPaginationSlice?.pageLimit,
    };
    dispatch(setTableLoading(true));
    rcaExecutionTableApi(resultVisulizationTestingApiRequestBody, authState)
      .then((response) => {
        const totalBackTestingPages = calculateTotalPages(
          PAGE_LIMIT,
          response?.totalCount
        );
        dispatch(
          updateExecutionBackTestingPageNumber({
            totalPages: totalBackTestingPages,
            pageLimit: rcaExecutionPaginationSlice.pageLimit,
            pageNumber: rcaExecutionPaginationSlice.pageNumber,
            isDataShownClicked: rcaExecutionPaginationSlice.isDataShownClicked,
          })
        );
        dispatch(setTableData(response?.rcaTableData));
        dispatch(setRcaStats(response?.rcaStats));
        dispatch(setRcaTags(response?.rcaTags));
        dispatch(setTableLoading(false));
      })
      .catch((error) => {
        console.log("rcaTableApi error", error);
      });
  };

  const makeSummaryCall = (
    forecastAnalysisApiRequestBodyTable: any,
    authState: any
  ) => {
    const resultVisulizationTestingApiRequestBody = {
      ...forecastAnalysisApiRequestBodyTable,
      pageNumber: rcaSummaryPaginationSlice?.pageNumber,
      pageSize: rcaSummaryPaginationSlice?.pageLimit,
    };
    dispatch(setTableLoading(true));
    rcaSummaryTableApi(resultVisulizationTestingApiRequestBody, authState).then(
      (response) => {
        const totalBackTestingPages = calculateTotalPages(
          PAGE_LIMIT,
          response?.totalCount
        );
        dispatch(
          updateSummaryBackTestingPageNumber({
            totalPages: totalBackTestingPages,
            pageLimit: rcaSummaryPaginationSlice.pageLimit,
            pageNumber: rcaSummaryPaginationSlice.pageNumber,
            isDataShownClicked: rcaSummaryPaginationSlice.isDataShownClicked,
          })
        );
        dispatch(
          setSecondTable({
            data: response?.data,
            totalCount: response?.totalCount,
          })
        );
        dispatch(setTableLoading(false));
      }
    );
  };

  return (
    <div className="p-5 bg-secondary-bg mt-4 rounded">
      {open && (
        <FilterResetModal
          open={open}
          resetFilter={resetFilter}
          handleClose={handleClose}
        />
      )}
      <FilterButtons handleOpen={handleOpen} applyFilters={applyFilters} />
      <FilterDropdown />
    </div>
  );
};

export default RcaExecutionFilter;
